// One explicit, user-approved guest-to-account handoff. No raw source is
// uploaded or attached to an account until the user chooses cloud save.
const DB = 'doarspace-guest-handoff-v1';
const STORE = 'pending';
const KEY = 'edited-model';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function openDatabase() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) return reject(Error('This browser cannot preserve a guest model during sign-in. Download the edited GLB first.'));
    const request = window.indexedDB.open(DB, 1);
    request.onupgradeneeded = () => { if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE); };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(Error('Browser storage is unavailable. Download the edited GLB before signing in.'));
    request.onblocked = () => reject(Error('Guest model storage is busy. Close other DoARspace tabs and retry.'));
  });
}
async function withStore(mode, action) {
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      let result;
      const request = action(transaction.objectStore(STORE));
      request.onsuccess = () => { result = request.result; };
      request.onerror = () => reject(Error('The browser could not preserve this model. Download the edited GLB first.'));
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(Error('Guest model storage could not complete. Download the edited GLB first.'));
      transaction.onabort = () => reject(Error('Guest model storage was interrupted. Your local editor is unchanged.'));
    });
  } finally { db.close(); }
}

export async function saveGuestHandoff(file, intent = 'save') {
  if (!(file instanceof Blob) || !file.size || file.size > 256 * 1024 * 1024) throw Error('The edited model cannot be stored for sign-in. Download its GLB before continuing.');
  try {
    await withStore('readwrite', store => store.put({
      blob: file, name: file.name || 'DoAR-edited.glb', size: file.size,
      type: file.type || 'model/gltf-binary', createdAt: Date.now(),
      intent: ['ai','publish'].includes(intent) ? intent : 'save'
    }, KEY));
  } catch (error) {
    throw Error((error?.message || 'Browser storage is full.') + ' Try downloading the edited GLB instead.');
  }
}
export async function loadGuestHandoffContext() {
  const record = await withStore('readonly', store => store.get(KEY));
  if (!record) return null;
  if (!record.createdAt || Date.now() - record.createdAt > MAX_AGE_MS) {
    await clearGuestHandoff(); return null;
  }
  if (!(record.blob instanceof Blob) || !record.blob.size || record.blob.size !== record.size) {
    throw Error('Saved guest model is incomplete. Re-import the exported GLB.');
  }
  return {
    file: new File([record.blob], record.name, {type: record.type, lastModified:record.createdAt}),
    intent: ['ai','publish'].includes(record.intent) ? record.intent : 'save'
  };
}
export async function loadGuestHandoff() {
  const context = await loadGuestHandoffContext();
  return context?.file || null;
}
export async function clearGuestHandoff() {
  await withStore('readwrite', store => store.delete(KEY));
}
